import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Download, Users } from "lucide-react";

dayjs.locale("fr");

export default function LeadsPage() {
  const branding = useBranding();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const navigate = useNavigate();

  const handleExport = async () => {
    try {
      const res = await api.get("/leads/export", {
        params: exportStatus ? { status: exportStatus } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "leads_export.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé.");
    } catch {
      toast.error("Export impossible pour le moment.");
    }
  };

  useEffect(() => {
    api
      .get("/leads", { params: { page } })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [page]);

  return (
    <div className="h-full overflow-y-auto chat-scroll px-5 md:px-8 py-6" data-testid="leads-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">Leads qualifiés</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {data.total} lead{data.total > 1 ? "s" : ""} prêt{data.total > 1 ? "s" : ""} à être recontacté{data.total > 1 ? "s" : ""}{branding.owner_name ? ` par ${branding.owner_name}` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={exportStatus}
            onChange={(e) => setExportStatus(e.target.value)}
            data-testid="export-status-filter"
            className="h-9 rounded-full border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="qualified">Qualifié</option>
            <option value="human_takeover">Humain</option>
          </select>
          <Button
            size="sm"
            onClick={handleExport}
            data-testid="export-leads-button"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-9"
          >
            <Download className="h-4 w-4 mr-1.5" />
            Exporter
          </Button>
        </div>
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.items.map((lead) => (
          <div
            key={lead.phone}
            data-testid={`lead-card-${lead.phone}`}
            className="bg-card rounded-xl border border-border p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-serif text-lg">{lead.nom || lead.profile_name || "Prospect"}</p>
                <p className="text-xs text-muted-foreground">{lead.phone}</p>
              </div>
              <span className="rounded-full bg-primary/15 text-primary text-xs font-semibold px-2.5 py-1">
                {lead.score}/100
              </span>
            </div>
            <dl className="mt-4 space-y-1.5 text-sm">
              <div className="flex gap-2"><dt className="w-20 text-muted-foreground shrink-0">Projet</dt><dd className="font-medium">{lead.projet || "—"}</dd></div>
              <div className="flex gap-2"><dt className="w-20 text-muted-foreground shrink-0">Secteur</dt><dd className="font-medium">{lead.secteur || "—"}</dd></div>
              <div className="flex gap-2"><dt className="w-20 text-muted-foreground shrink-0">Budget</dt><dd className="font-medium">{lead.budget || "—"}</dd></div>
              <div className="flex gap-2"><dt className="w-20 text-muted-foreground shrink-0">Délai</dt><dd className="font-medium">{lead.delai || "—"}</dd></div>
            </dl>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-[11px] text-muted-foreground">
                Qualifié le {lead.created_at ? dayjs(lead.created_at).format("D MMM YYYY à HH:mm") : "—"}
              </p>
              <Button
                size="sm"
                variant="outline"
                data-testid={`view-conversation-${lead.phone}`}
                onClick={() => navigate("/", { state: { phone: lead.phone } })}
                className="rounded-full text-xs h-8"
              >
                Voir la conversation
              </Button>
            </div>
          </div>
        ))}
      </div>

      {loaded && data.items.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-16">
          <Users className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Aucun lead qualifié pour l'instant.
          <br />
          Dès que l'agent collecte projet, secteur, budget et délai, le lead apparaît ici.
        </div>
      )}

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="leads-prev-page" className="rounded-full">
            Précédent
          </Button>
          <span className="text-xs text-muted-foreground">Page {data.page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="leads-next-page" className="rounded-full">
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
